import React, { useMemo, useState } from "react";

import { CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID } from "../../../../../../common/api/master-api/common/consts";
import { RecordType } from "../../../../../../common/types/RecordType";
import { CloudFileDetails } from "../../../CloudFileDetails";
import { useCloudFileDashboard } from "../../../common/context/useCloudFileDashboard";
import { useApplicableState } from "../hooks/useApplicableState";
import { CLOUD_FILE_ASSOCIATION_DEFAULT_TAB } from "../tabs/CloudFileAssociationTab";
import { shouldDisableCloudFileEdit } from "../utils/shouldDisableCloudFileEdit";
import {
    CloudFileAssociationContext,
    CloudFileAssociationFormState,
    CloudFileAssociationLanguagesFormState,
    CloudFileAssociationSectionItem,
    CloudFileAssociationVideoTechnicalDetailsFormState,
    defaultCloudFileAssociationContext
} from "./CloudFileAssociationContext";

type CloudFileAssociationContextProviderProps = React.PropsWithChildren<unknown>;

export const CloudFileAssociationContextProvider = (props: CloudFileAssociationContextProviderProps): JSX.Element => {
    const [cloudFileItem, setCloudFileItem] = useState<CloudFileAssociationSectionItem>();

    const [isEditDisabled, setIsEditDisabled] = useState(false);
    const [isEditForceDisabled, setIsEditForceDisabled] = useState(false);

    const { setIsAssociateFormReady } = useCloudFileDashboard();

    const [currentTab, setCurrentTab] = useState(CLOUD_FILE_ASSOCIATION_DEFAULT_TAB);

    const [isFormValid, setIsFormValid] = useState(true);

    const [selectedLanguages, setSelectedLanguages, applySelectedLanguages] = useApplicableState<
        CloudFileAssociationLanguagesFormState
    >(defaultCloudFileAssociationContext.selectedLanguages);

    const [
        selectedVideoTechnicalDetails,
        setSelectedVideoTechnicalDetails,
        applySelectedVideoTechnicalDetails
    ] = useApplicableState<CloudFileAssociationVideoTechnicalDetailsFormState>(
        defaultCloudFileAssociationContext.selectedVideoTechnicalDetails
    );

    const [isContextReady, setIsContextReady] = useState(false);

    const [defaultFormValues, setDefaultFormValues] = useState<CloudFileAssociationFormState | undefined>(undefined);

    const init = (details: CloudFileDetails, forceDisableEdit = false) => {
        setIsContextReady(false);
        setCloudFileItem(mapDetailsToCloudFileItem(details));

        const langs = mapCloudFileSelectedLanguages(details);
        setSelectedLanguages(langs);
        applySelectedLanguages(langs);

        const techDetails = mapVideoCloudFileSelectedTechnicalDetails(details);
        setSelectedVideoTechnicalDetails(techDetails);
        applySelectedVideoTechnicalDetails(techDetails);

        setIsEditForceDisabled(forceDisableEdit || shouldDisableCloudFileEdit(details));

        setIsContextReady(true);
        setIsAssociateFormReady(true);
    };

    const applyChanges = () => {
        applySelectedLanguages();
        applySelectedVideoTechnicalDetails();
    };

    const disableEdit = () => {
        setIsEditDisabled(true);
    };

    const enableEdit = () => {
        setIsEditDisabled(false);
    };

    const value = useMemo(
        () => ({
            init,
            isContextReady,

            isEditDisabled: isEditForceDisabled || isEditDisabled,
            disableEdit,
            enableEdit,

            currentTab,
            setCurrentTab,

            cloudFileItem,

            isFormValid,
            setIsFormValid,

            applyChanges,

            defaultFormValues,
            setDefaultFormValues,

            selectedLanguages,
            setSelectedLanguages,

            selectedVideoTechnicalDetails,
            setSelectedVideoTechnicalDetails
        }),
        [
            isContextReady,
            isEditDisabled,
            defaultFormValues,
            currentTab,
            cloudFileItem,
            isFormValid,
            selectedLanguages,
            selectedVideoTechnicalDetails
        ]
    );

    return <CloudFileAssociationContext.Provider value={value}>{props.children}</CloudFileAssociationContext.Provider>;
};

const mapDetailsToCloudFileItem = (details: CloudFileDetails) => ({
    id: details.id,
    status: details.status,
    recordType: details.recordType,
    content: details.content
});

const mapCloudFileSelectedLanguages = (details: CloudFileDetails) => ({
    subtitleLanguageId:
        details.languages.subtitleLanguageId ??
        (details.recordType === RecordType.Video ? CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID : null),
    soundtrackLanguageId: details.languages.soundtrackLanguageId ?? null
});

const mapVideoCloudFileSelectedTechnicalDetails = (details: CloudFileDetails) => ({
    aspectRatioId: details.videoTechnical?.aspectRatio?.id ?? null,
    aspectTypeId: details.videoTechnical?.aspectType?.id ?? null
});
